import {
  isMixedProduct,
  isMixedPourable,
  isOnPipe,
  productIsAlcoholic,
  type MachineProduct,
} from "../../api";

export type DrinkPreference = "alcoholic" | "non-alcoholic";

const NON_ALCOHOLIC_NAME_HINT =
  /(?:\b(?:non[- ]?alcoholic|alcohol[- ]?free|zero[- ]?alcohol|mocktail|ginger beer|root beer)\b|(?:^|\s)0(?:\.0)?%(?=\s|$))/i;
const ALCOHOLIC_NAME_HINT =
  /\b(?:alcohol|beer|wine|vodka|gin|rum|whisk(?:e)?y|tequila|liqueur|cognac|brandy|bourbon|vermouth|mezcal|schnapps|raki|rakı|arak|champagne|prosecco|cider)\b/i;

/**
 * Resolve the alcohol category conservatively from the live machine catalog.
 *
 * Some deployed catalogs mark mixed cocktails (and even spirits) as
 * `is_alcoholic: false`. Explicit positive metadata still wins, but recipe
 * components and well-known alcohol terms provide a safety net before a
 * negative flag is trusted. Products with no usable signal remain unknown and
 * are excluded from both preference pools.
 */
export function resolveAlcoholCategory(
  product: MachineProduct,
  catalog: Map<string, MachineProduct>,
  visited: Set<string> = new Set(),
): DrinkPreference | undefined {
  if (visited.has(product.product_id)) return undefined;
  const nextVisited = new Set(visited).add(product.product_id);

  if (productIsAlcoholic(product) === true) return "alcoholic";

  const searchableText = `${product.name} ${product.description ?? ""}`.trim();
  const containsAlcoholicComponent = (product.components ?? []).some((component) => {
    const ingredient = catalog.get(component.component_product_id);
    return ingredient
      ? resolveAlcoholCategory(ingredient, catalog, nextVisited) === "alcoholic"
      : ALCOHOLIC_NAME_HINT.test(component.name);
  });
  if (containsAlcoholicComponent) return "alcoholic";
  if (NON_ALCOHOLIC_NAME_HINT.test(searchableText)) return "non-alcoholic";
  if (ALCOHOLIC_NAME_HINT.test(searchableText)) return "alcoholic";

  if (productIsAlcoholic(product) === false) return "non-alcoholic";
  return undefined;
}

/** Return only currently pourable products in the category the guest chose. */
export function productsForPreference(
  allProducts: MachineProduct[],
  preference: DrinkPreference,
): MachineProduct[] {
  const catalog = new Map(allProducts.map((product) => [product.product_id, product]));

  return allProducts.filter((product) => {
    if (!product.is_enabled) return false;
    const pourable = isMixedProduct(product)
      ? isMixedPourable(product, catalog)
      : isOnPipe(product) && product.quantity_ml > 0 && (product.max_pour_ml ?? 1) > 0;
    if (!pourable) return false;
    return resolveAlcoholCategory(product, catalog) === preference;
  });
}
